import { useTranslation } from 'react-i18next';
import { useCallback, useMemo, useRef, useEffect, useState, ComponentProps } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { ReactEditor, useSlate, RenderElementProps } from 'slate-react';
import { Editor, Transforms, Element as SlateElement } from 'slate';
import { useEditorContext, useEditorMode } from 'app/state/contexts/EditorContext';
import { DataImage } from 'app/components';
import { useImage } from 'entities/document';
import { useSidebar } from 'app/state/contexts/SidebarContext';
import { insertStyledParagraphForImage } from 'slate/extensions/styled-paragraph';
import { useTagSelection } from 'app/state/contexts/TagContext';
import { useBlockTagColors, blockTagHighlightStyle } from 'slate/extensions/tag/utils/useBlockTagColors';
import { BlockTagBadge } from 'slate/extensions/tag/components/BlockTagBadge';
import { DeleteBlockModal } from 'slate/components/DeleteBlockModal';
import { useEditorDocument } from 'app/api/documents/useEditorData';
import { usePermissions } from 'app/api/permission-queries';
import { useEditorScope } from 'app/state/contexts/EditorScopeContext';
import { ImageElement } from 'slate/types/slate';
import { TagBinding } from 'slate/extensions/tag/types/block';

interface ImageData {
  file: ComponentProps<typeof DataImage>;
  metadata?: { width?: number; height?: number };
  occurences?: unknown;
}

interface ImagePlaceholderProps {
  width?: number;
  height?: number;
}

const ImagePlaceholder = (imageMetaData: ImagePlaceholderProps) => {
  const { t } = useTranslation('imageimport');

  const ratio = parseFloat(String(793 / (imageMetaData.width ?? 793))).toFixed(2);
  return (
    <div
      style={{
        width: '793px',
        height: `${Math.round(Number(ratio) * (imageMetaData.height ?? 100))}px`,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      {t('loading')}
    </div>
  );
};

type DocumentImageElement = ImageElement & { tags?: TagBinding[] };

type DocumentImageProps = Omit<RenderElementProps, 'element'> & {
  element: DocumentImageElement;
  readOnly?: boolean;
};

type SlateRef = ((node: HTMLDivElement | null) => void) | { current: HTMLDivElement | null } | null | undefined;

export const DocumentImage = (props: DocumentImageProps) => {
  const { t } = useTranslation('block');

  const editor = useSlate();
  const { imageSuperId, style, uuid: elementUuid, tags } = props.element;
  const { data: pDocument } = useEditorDocument();
  const image = pDocument?.content?.images?.find?.((image) => image.superId === imageSuperId);
  const { data: imageData, isLoading: isImageLoading } = useImage(image?.revisionId, 'screen') as { data?: ImageData; isLoading: boolean };
  const { setBlockSelection, clearBlockSelection, setClickedTagIds, blockSelection } = useTagSelection();
  const { setStyledParagraph, setIsSidebarOpen } = useSidebar();
  const { shadowHost } = useEditorContext();
  const { isTaggingMode, taggingSubMode } = useEditorMode();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const composedRef = useCallback(
    (node: HTMLDivElement | null) => {
      containerRef.current = node;
      const slateRef: SlateRef = props.attributes?.ref;
      if (typeof slateRef === 'function') slateRef(node);
      else if (slateRef && typeof slateRef === 'object') slateRef.current = node;
    },
    [props.attributes]
  );
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const performDelete = () => {
    const path = ReactEditor.findPath(editor, props?.element);
    Transforms.select(editor, path);
    Transforms.removeNodes(editor, { at: path });
  };
  const onAddStyledParagraph = () => {
    const path = ReactEditor.findPath(editor, props.element);
    const [existingWrapper] = Editor.nodes(editor, {
      at: path,
      match: (n) => SlateElement.isElement(n) && n.type === 'styled-paragraph',
    });
    if (existingWrapper) {
      setStyledParagraph?.({ ...existingWrapper[0] });
      setIsSidebarOpen?.(7);
    } else {
      const id = uuidv4();
      insertStyledParagraphForImage(editor, props.element, id);
      setStyledParagraph?.({ id, connected: true });
      setIsSidebarOpen?.(7);
    }
  };

  const confirmDelete = () => {
    setShowDeleteModal(false);
    performDelete();
  };

  const blockTagBindings = useMemo<TagBinding[]>(() => (Array.isArray(tags) ? tags : []), [tags]);
  const hasBlockTag = blockTagBindings.length > 0;

  const isEmbedded = !!useEditorScope()?.embedd;
  const { documentPermissions, sectionPermissions } = usePermissions(useEditorScope()?.documentId ?? '');
  const canEditTags = documentPermissions.includes('TAG_EDITOR') || documentPermissions.includes('TAG_ADMIN');
  const hasWritePermission = sectionPermissions[useEditorScope()?.sectionId ?? ''] === 'WRITE';
  const onImageClick = useCallback(
    (e: React.MouseEvent) => {
      if (props.readOnly) return;
      if ((e.target as Element).closest && (e.target as Element).closest('.controls--container')) return;
      // Coverage mode: images are read-only (not a coverage target, no tag editing).
      if (taggingSubMode === 'mark-coverage') return;
      if (hasBlockTag) {
        setClickedTagIds?.(
          blockTagBindings.map((b) => b.tagId),
          { fromContent: true }
        );
        setIsSidebarOpen?.(8);
      }
      try {
        const path = ReactEditor.findPath(editor, props.element);
        let uuid = elementUuid;
        if (!uuid) {
          uuid = uuidv4();
          Transforms.setNodes(editor, { uuid } as Partial<ImageElement>, { at: path });
        }
        setBlockSelection?.({ kind: 'image', path, elementId: uuid });
        setIsSidebarOpen?.(8);
      } catch {
        console.warn(`Path lookup failed`);
      }
    },
    [props.readOnly, props.element, hasBlockTag, blockTagBindings, setBlockSelection, setClickedTagIds, setIsSidebarOpen, editor, elementUuid, taggingSubMode]
  );

  const isBlockSelected = !!elementUuid && blockSelection?.kind === 'image' && blockSelection.elementId === elementUuid;

  useEffect(() => {
    if (!shadowHost?.shadowRoot || !isBlockSelected) return;
    const root = shadowHost.shadowRoot;
    const handleClickOutside = (event: Event) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node | null)) {
        clearBlockSelection?.();
      }
    };
    root.addEventListener('mousedown', handleClickOutside);
    return () => {
      root.removeEventListener('mousedown', handleClickOutside);
    };
  }, [shadowHost, isBlockSelected, clearBlockSelection]);
  const blockTagClass = isBlockSelected ? ' editor-image--container--block-tagged' : '';

  const blockTagColors = useBlockTagColors(hasBlockTag ? blockTagBindings : undefined);
  const showBlockTagVisual = hasBlockTag && !isEmbedded && isTaggingMode && blockTagColors && !blockTagColors.isHidden;
  const blockTagStyle = {
    ...(showBlockTagVisual ? blockTagHighlightStyle(blockTagColors) : {}),
    ...(isBlockSelected && blockTagColors ? { outlineColor: blockTagColors.indicator } : {}),
  };

  return (
    <div
      {...props.attributes}
      ref={composedRef}
      className={`editor-image--container${blockTagClass}`}
      style={{ ...style, ...blockTagStyle } as React.CSSProperties}
      data-element-uuid={elementUuid}
      onClick={onImageClick}
      contentEditable={false}
    >
      {!props.readOnly && (
        <div className="controls--container">
          {hasWritePermission && (
            <button onClick={onAddStyledParagraph}>
              <i className="fa-regular fa-rectangle-list" style={{ fontFamily: '"Font Awesome 6 Pro"' }} />
            </button>
          )}
          {hasWritePermission && (
            <button
              className="controls--btn--danger"
              onClick={(e) => {
                e.stopPropagation();
                setShowDeleteModal(true);
              }}
            >
              <i className="fa-regular fa-trash-can" style={{ fontFamily: '"Font Awesome 6 Pro"' }} />
            </button>
          )}
        </div>
      )}
      <DeleteBlockModal show={showDeleteModal} message={t('delete.modal.image')} onConfirm={confirmDelete} onCancel={() => setShowDeleteModal(false)} />
      {hasBlockTag && !isEmbedded && isTaggingMode && (
        <BlockTagBadge
          bindings={blockTagBindings}
          tooltipKey="tag:block.image.tagged"
          canEdit={canEditTags}
          onContinueClick={(firstTagId: string) => {
            clearBlockSelection?.();
            setClickedTagIds?.([firstTagId]);
            setIsSidebarOpen?.(8);
          }}
          onSelectTag={(tagId: string) => {
            clearBlockSelection?.();
            setClickedTagIds?.([tagId]);
            setIsSidebarOpen?.(8);
          }}
          onOpen={() => {
            clearBlockSelection?.();
            setClickedTagIds?.(blockTagBindings.map((b) => b.tagId));
            setIsSidebarOpen?.(8);
          }}
        />
      )}
      {isImageLoading && <ImagePlaceholder {...imageData?.metadata} />}
      {imageData ? <DataImage {...(imageData.file as ComponentProps<typeof DataImage>)} /> : !isImageLoading ? <i className="fa-regular fa-image" /> : null}
      {props.children}
    </div>
  );
};
